import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, ChevronDown, ChevronUp, MapPin, Calendar, Search, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function BrowseCommunityTripsModal({ isOpen, onClose, destination, destinations, onAddActivity }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [addedActivities, setAddedActivities] = useState(new Set());
  const [selectedDest, setSelectedDest] = useState('');

  // Build list of available destinations
  const destOptions = destinations && destinations.length > 1
    ? destinations
    : destination ? [destination] : [];

  // Initialize selected destination
  useEffect(() => {
    if (!isOpen) return;
    if (destOptions.length > 0 && !selectedDest) {
      setSelectedDest(destOptions[0]);
    }
  }, [isOpen, destOptions, selectedDest]);

  const fetchTrips = useCallback(async (query, dest) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '15' });
      if (query) params.append('search', query);
      else if (dest) params.append('destination', dest);
      else if (destination) params.append('destination', destination);

      const res = await fetch(`${BACKEND_URL}/api/community/itineraries/browse?${params}`);
      if (res.ok) {
        const data = await res.json();
        const results = data.itineraries || [];

        // Filter out trips with empty itinerary_data — they render as
        // "0 days" cards with no expandable content (mobile parity:
        // mobile/components/community/BrowseCommunityTripsModal.js).
        const nonEmpty = results.filter((t) => {
          const days = t?.itinerary_data?.days;
          return Array.isArray(days) && days.length > 0;
        });

        // Tighter destination matching: server's ILIKE leaks for short
        // terms like "Thailand" matching unrelated titles. Require the
        // search term to actually appear in destination/title.
        const filterTerm = (query || dest || destination || '').trim().toLowerCase();
        const final = filterTerm
          ? nonEmpty.filter((t) => {
              const haystack = `${t?.destination || ''} ${t?.title || ''}`.toLowerCase();
              return haystack.includes(filterTerm);
            })
          : nonEmpty;

        setTrips(final);
      }
    } catch (err) {
      console.error('Browse community trips error:', err);
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, [destination]);

  useEffect(() => {
    if (!isOpen) return;
    fetchTrips(searchQuery, selectedDest);
  }, [isOpen, selectedDest]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTrips(searchQuery, selectedDest);
  };

  const handleAddActivity = (activityName) => {
    onAddActivity(activityName);
    setAddedActivities(prev => new Set([...prev, activityName]));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[75vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">Community Trips</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Add activities from other travelers' itineraries
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Destination Picker — only for multi-destination trips */}
        {destOptions.length > 1 && (
          <div className="px-5 pt-3 flex gap-2 flex-wrap">
            {destOptions.map((dest) => {
              const isActive = selectedDest === dest;
              const label = dest.split(',')[0].trim();
              return (
                <button
                  key={dest}
                  onClick={() => { setSelectedDest(dest); setSearchQuery(''); }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    isActive
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300 hover:text-teal-600'
                  }`}
                >
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Search */}
        <form onSubmit={handleSearch} className="px-5 py-3 border-b border-gray-100">
          <div className="flex gap-2">
            <div className="flex-1 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <Search className="w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search trips${selectedDest ? ` near ${selectedDest.split(',')[0]}` : ''}...`}
                className="flex-1 bg-transparent text-sm outline-none"
              />
            </div>
            <button type="submit" className="px-3 py-2 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700">
              Search
            </button>
          </div>
        </form>

        {/* Trip list */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 w-40 bg-gray-200 rounded mb-2" />
                  <div className="h-3 w-24 bg-gray-100 rounded" />
                </div>
              ))}
            </div>
          ) : trips.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No community trips found</p>
              <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="space-y-2">
              {trips.map((trip) => {
                const isExpanded = expandedTrip === trip.id;
                const days = trip.itinerary_data?.days || [];
                const tripDest = trip.destination || trip.title || 'Unknown';

                return (
                  <div key={trip.id} className="border border-gray-200 rounded-xl overflow-hidden">
                    {/* Trip header */}
                    <button
                      onClick={() => setExpandedTrip(isExpanded ? null : trip.id)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                          <MapPin className="w-4 h-4 text-teal-600" />
                        </div>
                        <div className="text-left min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{tripDest}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <Calendar className="w-3 h-3" />
                            <span>{days.length} days</span>
                            {trip.user_display_name && (
                              <span>by {trip.user_display_name}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </button>

                    {/* Expanded: show activities by day */}
                    {isExpanded && days.length > 0 && (
                      <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
                        {days.map((day, dayIdx) => (
                          <div key={dayIdx} className="mb-3 last:mb-0">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                              Day {day.day || dayIdx + 1} — {day.title || day.baseCity || ''}
                            </p>
                            <div className="space-y-1">
                              {(day.schedule || []).map((act, actIdx) => {
                                const name = act.activity || act.name || '';
                                if (!name) return null;
                                const isAdded = addedActivities.has(name);
                                return (
                                  <div key={actIdx} className="flex items-center justify-between gap-2 py-1 px-2 rounded-lg hover:bg-white transition-colors">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-medium text-gray-800 truncate">{name}</p>
                                      {act.time && <span className="text-[10px] text-gray-400">{act.time}</span>}
                                    </div>
                                    <button
                                      onClick={() => handleAddActivity(name)}
                                      disabled={isAdded}
                                      className={`shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                                        isAdded
                                          ? 'bg-teal-50 text-teal-600 border border-teal-200'
                                          : 'bg-white text-gray-500 border border-gray-200 hover:border-teal-300 hover:text-teal-600'
                                      }`}
                                    >
                                      {isAdded ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                      {isAdded ? 'Added' : 'Add'}
                                    </button>
                                  </div>
                                );
                              })}
                              {/* Meals */}
                              {(Array.isArray(day.meals) ? day.meals : Object.values(day.meals || {})).map((meal, mIdx) => {
                                const name = meal.restaurant || meal.meal || '';
                                if (!name || /^(breakfast|lunch|dinner)$/i.test(name)) return null;
                                const isAdded = addedActivities.has(name);
                                return (
                                  <div key={`meal-${mIdx}`} className="flex items-center justify-between gap-2 py-1 px-2 rounded-lg hover:bg-white transition-colors">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-medium text-gray-800 truncate">{name}</p>
                                      {meal.cuisine && <span className="text-[10px] text-orange-500">{meal.cuisine}</span>}
                                    </div>
                                    <button
                                      onClick={() => handleAddActivity(`Dinner at ${name}`)}
                                      disabled={isAdded}
                                      className={`shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                                        isAdded
                                          ? 'bg-teal-50 text-teal-600 border border-teal-200'
                                          : 'bg-white text-gray-500 border border-gray-200 hover:border-teal-300 hover:text-teal-600'
                                      }`}
                                    >
                                      {isAdded ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                      {isAdded ? 'Added' : 'Add'}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
